import { useCallback } from 'react';
import { api } from '@/redux/api';
import { AccessibilityPolicySettings } from '@/types/accessibility-policy-settings';
import { AlexaSkillPolicy } from '@/types/alexa-skill-policy';
import { Policy } from '@/types/policy';
import { WellbeingCheckSettings } from '@/types/wellbeing-check-settings';
import { useCurrentOrganisation } from './use-current-organisation';

export const usePolicies = () => {
  const { organisation } = useCurrentOrganisation();

  return api.useGetPoliciesQuery(
    { organisation: organisation! },
    {
      skip: organisation === undefined,
    },
  );
};

export const useUpdateAlexaSkillMutation = (policies: Policy | undefined) => {
  const [updatePoliciesAlexaSkills] =
    api.useUpdatePoliciesAlexaSkillsMutation();

  const update = useCallback(
    async (skill: AlexaSkillPolicy, enabled: boolean) => {
      if (policies) {
        const skills = [...policies.alexaSkills];
        const skillIndex = skills.findIndex(
          ({ skillId }) => skill.skillId === skillId,
        );
        if (skillIndex > -1) {
          skills[skillIndex] = {
            ...skills[skillIndex],
            enabled,
          };
          return await updatePoliciesAlexaSkills({
            organisation: policies.organisation,
            alexaSkills: skills,
          });
        }
      }
      return { data: undefined, error: { message: 'unable to update skill' } }; // TODO: confirm error handled correctly
    },
    [policies, updatePoliciesAlexaSkills],
  );

  return [update];
};

export const useUpdateWellbeingCheckSettingsMutation = (
  policies: Policy | undefined,
) => {
  const [updatePoliciesWellbeingCheckSettings] =
    api.useUpdatePoliciesWellbeingCheckSettingsMutation();

  const update = useCallback(
    async (wellbeingCheckSettings: WellbeingCheckSettings) => {
      if (policies) {
        return await updatePoliciesWellbeingCheckSettings({
          organisation: policies.organisation,
          wellbeingCheckSettings,
        });
      }
      return {
        data: undefined,
        error: { message: 'unable to update settings' },
      }; // TODO: confirm error handled correctly
    },
    [policies, updatePoliciesWellbeingCheckSettings],
  );

  return [update];
};

export const useUpdateAccessibilityPolicySettingsMutation = (
  policies: Policy | undefined,
) => {
  const [updatePoliciesAccessiblitySettings] =
    api.useUpdatePoliciesAccessibilitySettingsMutation();

  const update = useCallback(
    async (accessibilityPolicySettings: AccessibilityPolicySettings) => {
      if (policies) {
        return await updatePoliciesAccessiblitySettings({
          organisation: policies.organisation,
          accessibility: accessibilityPolicySettings,
        });
      }
      return {
        data: undefined,
        error: { message: 'unable to update settings' },
      }; // TODO: confirm error handled correctly
    },
    [policies, updatePoliciesAccessiblitySettings],
  );

  return [update];
};
